import { useEffect, useState } from 'react';
import {
  Loader2,
  Phone,
  Mail,
  User,
  Clock,
  CalendarClock,
  Pencil,
  X,
  Check,
  Ban,
  BellRing,
  ExternalLink,
  MessageSquare,
  Video,
  ShieldCheck,
} from 'lucide-react';
import apiClient from '@/lib/api';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@easyflow/ui-frontend/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-frontend/components/ui/dialog';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';
import AppointmentSourceBadge from '@/components/calendar/AppointmentSourceBadge';
import type { AppointmentSourceKind } from '@/lib/appointment-source-taxonomy';
import type { CalendarAccess } from '@easyflow/shared';
import { canWriteCalendarObject } from '@/lib/calendar-access';

export interface AppointmentLite {
  user_id?: string | null;
  person_id?: string | null;
  id: string;
  title?: string;
  start_time: string;
  end_time: string;
  status: string;
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  customer_id?: string | null;
  lead_id?: string | null;
  assigned_to_name?: string | null;
  assigned_to_id?: string | null;
  user_name?: string | null;
  type_name?: string | null;
  type_color?: string | null;
  notes?: string | null;
  internal_notes?: string | null;
  created_at?: string;
  updated_at?: string;
  meeting_link?: string | null;
  metadata?: Record<string, unknown>;
  // Task #595: tassonomia unificata sorgente.
  source_kind?: AppointmentSourceKind | null;
  // Task #1214: documento privacy effettivamente accettato (denormalizzato sul
  // record dal task #1211). Permette audit GDPR puntuale per prenotazione.
  privacy_document_id?: string | null;
  privacy_document_version?: number | null;
  privacy_document_title?: string | null;
  privacy_document_published_at?: string | null;
}

interface PrivacyDocumentPayload {
  id: string;
  title: string | null;
  content: string;
  version: number;
  published_at: string | null;
  is_active: boolean;
}

const STATUS_LABELS: Record<string, string> = {
  scheduled: 'Programmato',
  confirmed: 'Confermato',
  reminded: 'Promemoria inviato',
  rescheduled: 'Riprogrammato',
  cancelled: 'Annullato',
  completed: 'Completato',
  no_show: 'Non presentato',
};

const STATUS_TONE: Record<string, string> = {
  scheduled: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300',
  confirmed: 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-300',
  reminded: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300',
  rescheduled: 'bg-violet-100 text-violet-700 border-violet-300 dark:bg-violet-900/30 dark:text-violet-300',
  cancelled: 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-300',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300',
  no_show: 'bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-300',
};

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('it-IT', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

function durationMin(startIso: string, endIso: string): number {
  return Math.max(0, Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000));
}

export interface AppointmentDetailPanelProps {
  access?: CalendarAccess | null;
  canManage?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string | null;
  /** Optional initial data while we fetch the full record. */
  initial?: AppointmentLite | null;
  onEdit?: (appt: AppointmentLite) => void;
  onReschedule?: (appt: AppointmentLite) => void;
  onOpenCustomer?: (appt: AppointmentLite) => void;
  onChanged?: () => void;
}

export default function AppointmentDetailPanel({
  access = null,
  canManage: callerCanManage = false,
  open,
  onOpenChange,
  appointmentId,
  initial,
  onEdit,
  onReschedule,
  onOpenCustomer,
  onChanged,
}: AppointmentDetailPanelProps) {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [appt, setAppt] = useState<AppointmentLite | null>(initial || null);
  const canManage = callerCanManage && !!appt && canWriteCalendarObject(access, appt, 'edit');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  // Task #1214: anteprima del documento privacy storico accettato.
  const [privacyDoc, setPrivacyDoc] = useState<PrivacyDocumentPayload | null>(null);
  const [privacyDocOpen, setPrivacyDocOpen] = useState(false);
  const [privacyDocLoading, setPrivacyDocLoading] = useState(false);

  async function openPrivacyDoc() {
    if (!appt?.id) return;
    setPrivacyDocOpen(true);
    if (privacyDoc && privacyDoc.id === appt.privacy_document_id) return;
    setPrivacyDoc(null);
    setPrivacyDocLoading(true);
    try {
      const res = await apiClient.get<PrivacyDocumentPayload>(
        `/calendar/appointments/${appt.id}/privacy-document`,
      );
      if (res.success && res.data) setPrivacyDoc(res.data);
      else throw new Error(res.error || 'Documento non disponibile');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Documento non disponibile';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
      setPrivacyDocOpen(false);
    } finally {
      setPrivacyDocLoading(false);
    }
  }

  useEffect(() => {
    if (!open || !appointmentId) return;
    let cancelled = false;
    setLoading(true);
    setAppt(initial || null);
    apiClient
      .get<AppointmentLite>(`/calendar/appointments/${appointmentId}`)
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) setAppt(res.data);
      })
      .catch(() => {
        if (!cancelled) {
          toast({ title: 'Errore', description: 'Caricamento appuntamento fallito', variant: 'destructive' });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, appointmentId, initial, toast]);

  async function updateStatus(next: string, label: string) {
    if (!canManage) return;
    if (!appt) return;
    setActionLoading(next);
    try {
      const res = await apiClient.put(`/calendar/appointments/${appt.id}`, { status: next });
      if (!res.success) throw new Error(res.error || 'Aggiornamento fallito');
      setAppt({ ...appt, status: next });
      toast({ title: label });
      onChanged?.();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Aggiornamento fallito';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  }

  function sendReminder(channel: 'whatsapp' | 'sms' | 'email') {
    if (!canManage) return;
    if (!appt) return;
    const phone = appt.customer_phone || '';
    const email = appt.customer_email || '';
    const startStr = fmtDateTime(appt.start_time);
    const body = `Promemoria appuntamento: ${appt.type_name || appt.title || ''} il ${startStr}.`;
    if (channel === 'email') {
      if (!email) {
        toast({ title: 'Email mancante', variant: 'destructive' });
        return;
      }
      window.open(`mailto:${email}?subject=${encodeURIComponent('Promemoria appuntamento')}&body=${encodeURIComponent(body)}`);
    } else if (channel === 'sms') {
      if (!phone) {
        toast({ title: 'Telefono mancante', variant: 'destructive' });
        return;
      }
      window.open(`sms:${phone}?body=${encodeURIComponent(body)}`);
    } else {
      if (!phone) {
        toast({ title: 'Telefono mancante', variant: 'destructive' });
        return;
      }
      const cleaned = phone.replace(/[^\d+]/g, '');
      window.open(`https://wa.me/${cleaned.replace(/^\+/, '')}?text=${encodeURIComponent(body)}`, '_blank');
    }
  }

  const side = isMobile ? 'bottom' : 'right';
  const a = appt;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={side}
        className={cn(
          'overflow-y-auto p-0',
          isMobile
            ? 'h-[85vh] rounded-t-2xl'
            : 'w-full sm:max-w-lg'
        )}
      >
        {!a ? (
          <div className="flex items-center justify-center py-16">
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            ) : (
              <span className="text-sm text-gray-500">Nessun appuntamento selezionato</span>
            )}
          </div>
        ) : (
          <>
            <div
              className="px-5 pt-5 pb-3 border-b border-gray-200 dark:border-gray-700"
              style={a.type_color ? { borderTop: `4px solid ${a.type_color}` } : undefined}
            >
              <SheetHeader className="text-left">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <SheetTitle className="text-xl truncate">
                      {a.customer_name || a.title || 'Appuntamento'}
                    </SheetTitle>
                    <SheetDescription className="mt-1 flex items-center gap-2 flex-wrap">
                      {a.type_color && (
                        <span
                          className="inline-block w-2 h-2 rounded-full"
                          style={{ backgroundColor: a.type_color }}
                        />
                      )}
                      <span>{a.type_name || 'Servizio non specificato'}</span>
                      <Badge
                        variant="outline"
                        className={cn('text-[11px] border', STATUS_TONE[a.status] || 'bg-gray-100 text-gray-700 border-gray-300')}
                      >
                        {STATUS_LABELS[a.status] || a.status}
                      </Badge>
                      {a.source_kind && (
                        <AppointmentSourceBadge kind={a.source_kind} className="text-[11px]" />
                      )}
                    </SheetDescription>
                  </div>
                </div>
              </SheetHeader>
            </div>

            <div className="px-5 py-4 space-y-5">
              {/* Quando */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5">
                  <CalendarClock className="w-3.5 h-3.5" /> Quando
                </h4>
                <div className="text-sm text-gray-900 dark:text-gray-100">
                  {fmtDateTime(a.start_time)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {fmtTime(a.start_time)} – {fmtTime(a.end_time)}
                  <span className="ml-2">· {durationMin(a.start_time, a.end_time)} min</span>
                </div>
              </section>

              {/* Chi */}
              {(a.assigned_to_name || a.user_name) && (
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" /> Consulente
                  </h4>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-xs font-semibold">
                      {(a.assigned_to_name || a.user_name || '?')
                        .split(' ')
                        .map((s) => s[0])
                        .filter(Boolean)
                        .slice(0, 2)
                        .join('')
                        .toUpperCase()}
                    </div>
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {a.assigned_to_name || a.user_name}
                    </div>
                  </div>
                </section>
              )}

              {/* Cliente */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Cliente
                </h4>
                <div className="space-y-1.5">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {a.customer_name || '—'}
                  </div>
                  {a.customer_phone && (
                    <a
                      href={`tel:${a.customer_phone}`}
                      className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline min-h-[44px]"
                    >
                      <Phone className="w-4 h-4" /> {a.customer_phone}
                    </a>
                  )}
                  {a.customer_email && (
                    <a
                      href={`mailto:${a.customer_email}`}
                      className="flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline min-h-[44px] break-all"
                    >
                      <Mail className="w-4 h-4 shrink-0" /> {a.customer_email}
                    </a>
                  )}
                  {(a.person_id || a.customer_id || a.lead_id) && onOpenCustomer && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-1 h-9"
                      onClick={() => onOpenCustomer(a)}
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Apri scheda cliente
                    </Button>
                  )}
                </div>
              </section>

              {/* Task #649: Prodotto / Servizio collegato (metadata.product_*) */}
              {(() => {
                const m = (a.metadata || {});
                const productName = (m.product_name as string) || (m.product_id ? 'Prodotto' : '');
                if (!productName) return null;
                const qty = (m.product_quantity as number) || 1;
                const price = m.product_price as number | undefined;
                const note = m.product_note as string | undefined;
                return (
                  <section>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Prodotto / Servizio
                    </h4>
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {productName}
                      {qty > 1 ? ` × ${qty}` : ''}
                      {typeof price === 'number' ? ` — € ${Number(price).toFixed(2)}` : ''}
                    </div>
                    {note && (
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{note}</div>
                    )}
                  </section>
                );
              })()}

              {/* Task #649: Priorità (metadata.priority) */}
              {(() => {
                const m = (a.metadata || {});
                const p = (m.priority as string) || '';
                if (!p || p === 'normal') return null;
                const tone =
                  p === 'urgent'
                    ? 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-300'
                    : p === 'high'
                      ? 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300'
                      : 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800/50 dark:text-gray-300';
                const label = p === 'urgent' ? 'Urgente' : p === 'high' ? 'Alta' : p === 'low' ? 'Bassa' : p;
                return (
                  <section>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Priorità
                    </h4>
                    <Badge variant="outline" className={cn('text-[11px] border', tone)}>
                      {label}
                    </Badge>
                  </section>
                );
              })()}

              {/* Task #649: Campi modulo (metadata.form_values) */}
              {(() => {
                const m = (a.metadata || {});
                const fv = m.form_values as Record<string, unknown> | undefined;
                if (!fv || typeof fv !== 'object') return null;
                const entries = Object.entries(fv);
                if (entries.length === 0) return null;
                return (
                  <section>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Campi modulo
                    </h4>
                    <dl className="text-sm space-y-1">
                      {entries.map(([k, v]) => (
                        <div key={k} className="grid grid-cols-3 gap-2">
                          <dt className="text-xs text-gray-500 dark:text-gray-400 truncate">{k}</dt>
                          <dd className="col-span-2 text-gray-900 dark:text-gray-100 break-words">
                            {typeof v === 'boolean' ? (v ? 'Sì' : 'No') : String(v ?? '')}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                );
              })()}

              {/* Task #649: Reminder pianificati (metadata.scheduled_reminders) */}
              {(() => {
                const m = (a.metadata || {});
                const sr = m.scheduled_reminders as Array<{ channel: string; offset_minutes: number }> | undefined;
                if (!Array.isArray(sr) || sr.length === 0) return null;
                const fmt = (mins: number) =>
                  mins >= 1440 ? `${Math.round(mins / 1440)}g` : mins >= 60 ? `${Math.round(mins / 60)}h` : `${mins}m`;
                return (
                  <section>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5">
                      <BellRing className="w-3.5 h-3.5" /> Reminder pianificati
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {sr.map((r, i) => (
                        <Badge key={`${r.channel}-${r.offset_minutes}-${i}`} variant="outline" className="text-[11px] capitalize">
                          {r.channel} · {fmt(r.offset_minutes)}
                        </Badge>
                      ))}
                    </div>
                  </section>
                );
              })()}

              {/* Task #1214: informativa privacy accettata (audit GDPR) */}
              {a.privacy_document_id && a.privacy_document_version != null && (
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5" /> Informativa privacy
                  </h4>
                  <button
                    type="button"
                    onClick={openPrivacyDoc}
                    className="inline-flex items-center gap-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:underline text-left"
                    title="Apri anteprima del documento accettato"
                  >
                    <span>
                      Informativa accettata: <span className="font-semibold">v{a.privacy_document_version}</span>
                      {a.privacy_document_published_at && (
                        <> del {new Date(a.privacy_document_published_at).toLocaleDateString('it-IT')}</>
                      )}
                    </span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                  {a.privacy_document_title && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                      {a.privacy_document_title}
                    </div>
                  )}
                </section>
              )}

              {/* Note */}
              {(a.notes || a.internal_notes) && (
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                    Note
                  </h4>
                  {a.notes && (
                    <div className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{a.notes}</div>
                  )}
                  {a.internal_notes && (
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap p-2 rounded bg-gray-50 dark:bg-gray-800">
                      <span className="font-semibold">Interno:</span> {a.internal_notes}
                    </div>
                  )}
                </section>
              )}

              {/* Meeting link */}
              {a.meeting_link && (
                <section>
                  <a
                    href={a.meeting_link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline min-h-[44px]"
                  >
                    <Video className="w-4 h-4" /> Apri link videocall
                  </a>
                </section>
              )}

              {/* Cronologia */}
              {(a.created_at || a.updated_at) && (
                <section className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                  {a.created_at && <div>Creato il {new Date(a.created_at).toLocaleString('it-IT')}</div>}
                  {a.updated_at && a.updated_at !== a.created_at && (
                    <div>Ultima modifica {new Date(a.updated_at).toLocaleString('it-IT')}</div>
                  )}
                </section>
              )}

              {/* Azioni rapide */}
              <section className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Azioni
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {canManage && onEdit && (
                    <Button variant="outline" size="sm" className="h-11 justify-start" onClick={() => onEdit(a)}>
                      <Pencil className="w-4 h-4 mr-2" /> Modifica
                    </Button>
                  )}
                  {canManage && onReschedule && (
                    <Button variant="outline" size="sm" className="h-11 justify-start" onClick={() => onReschedule(a)}>
                      <CalendarClock className="w-4 h-4 mr-2" /> Riprogramma
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-11 justify-start"
                    disabled={!canManage || a.status === 'completed' || actionLoading === 'completed'}
                    onClick={() => updateStatus('completed', 'Marcato come completato')}
                  >
                    {actionLoading === 'completed' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Completato
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-11 justify-start"
                    disabled={!canManage || a.status === 'no_show' || actionLoading === 'no_show'}
                    onClick={() => updateStatus('no_show', 'Marcato come no-show')}
                  >
                    {actionLoading === 'no_show' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Ban className="w-4 h-4 mr-2" />
                    )}
                    No-show
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-11 justify-start text-red-600 hover:text-red-700"
                    disabled={!canManage || a.status === 'cancelled' || actionLoading === 'cancelled'}
                    onClick={() => updateStatus('cancelled', 'Appuntamento annullato')}
                  >
                    {actionLoading === 'cancelled' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <X className="w-4 h-4 mr-2" />
                    )}
                    Annulla
                  </Button>
                </div>
                <div className="mt-3">
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-1.5 inline-flex items-center gap-1">
                    <BellRing className="w-3 h-3" /> Invia promemoria
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10"
                      disabled={!canManage || actionLoading === 'reminder-whatsapp'}
                      onClick={() => sendReminder('whatsapp')}
                    >
                      <MessageSquare className="w-3.5 h-3.5 mr-1" /> WhatsApp
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10"
                      disabled={!canManage || actionLoading === 'reminder-sms'}
                      onClick={() => sendReminder('sms')}
                    >
                      SMS
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10"
                      disabled={!canManage || actionLoading === 'reminder-email'}
                      onClick={() => sendReminder('email')}
                    >
                      <Mail className="w-3.5 h-3.5 mr-1" /> Email
                    </Button>
                  </div>
                </div>
              </section>
            </div>
          </>
        )}
      </SheetContent>
      {/* Task #1214: anteprima documento privacy storico accettato */}
      <Dialog open={privacyDocOpen} onOpenChange={setPrivacyDocOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              {privacyDoc?.title || 'Informativa privacy'}
            </DialogTitle>
            <DialogDescription>
              {privacyDoc ? (
                <>
                  Versione <span className="font-semibold">v{privacyDoc.version}</span>
                  {privacyDoc.published_at && (
                    <> · pubblicata il {new Date(privacyDoc.published_at).toLocaleDateString('it-IT')}</>
                  )}
                  {!privacyDoc.is_active && <> · <span className="text-amber-600 dark:text-amber-400">non più attiva</span></>}
                </>
              ) : (
                'Caricamento documento…'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 mt-2">
            {privacyDocLoading || !privacyDoc ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : (
              <div
                className="prose prose-sm dark:prose-invert max-w-none text-sm"
                dangerouslySetInnerHTML={{ __html: privacyDoc.content }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}
